<template>
    <div class="rounded-xl border border-gray-200 bg-white p-5">
        <div class="flex flex-wrap items-start justify-between gap-4">
            <div>
                <p
                    class="text-xs font-medium uppercase tracking-wide text-gray-400"
                >
                    {{ t("vue.kiosk_ai_quota_title") }}
                </p>
                <p
                    v-if="collapsed"
                    class="mt-1 text-base font-medium text-gray-500"
                >
                    {{ t("vue.kiosk_ai_quota_state_not_applicable") }}
                </p>
                <template v-else>
                    <p class="mt-1 text-2xl font-semibold text-gray-800">
                        {{ formatCount(quota.used) }} /
                        {{ formatCount(quota.limit) }}
                    </p>
                    <p class="mt-1 text-xs text-gray-400">
                        {{
                            t("vue.kiosk_ai_quota_composition", {
                                base: formatCount(quota.default_limit),
                                extra: formatCount(quota.extra),
                            })
                        }}
                    </p>
                </template>
            </div>

            <div v-if="!collapsed" class="flex flex-col items-end gap-1">
                <span
                    class="inline-flex rounded-md px-2 py-[3px] text-xs font-medium"
                    :class="stateClass"
                >
                    {{ stateLabel }}
                </span>
                <span class="text-xs text-gray-400">
                    {{
                        t("vue.kiosk_ai_quota_remaining", {
                            count: formatCount(quota.remaining),
                        })
                    }}
                </span>
            </div>

            <button
                v-if="isApplicable === false"
                type="button"
                class="text-xs font-medium text-gray-500 underline hover:text-gray-700"
                @click="expanded = !expanded"
            >
                {{
                    expanded
                        ? t("vue.kiosk_ai_quota_hide_details")
                        : t("vue.kiosk_ai_quota_show_details")
                }}
            </button>
        </div>

        <p v-if="reason" class="mt-4 text-xs text-gray-400">
            {{ reason }}
            <Link
                v-if="quota.action_state === 'needs_trial_extension'"
                :href="
                    route('kiosk.teams.feature-management.index', {
                        team: teamId,
                    })
                "
                class="font-medium text-gray-600 underline hover:text-gray-800"
            >
                {{ t("vue.kiosk_ai_quota_extend_trial_link") }}
            </Link>
        </p>

        <div v-if="!collapsed" class="mt-4 flex flex-wrap items-end gap-3">
            <div>
                <label
                    for="ai_quota_amount"
                    class="block text-xs font-medium text-gray-500"
                >
                    {{ t("vue.kiosk_ai_quota_amount") }}
                </label>
                <NumberInput
                    id="ai_quota_amount"
                    v-model="amount"
                    class="mt-1"
                    :min="MIN_GRANT"
                    :max="MAX_GRANT"
                />
            </div>

            <AtomicButton
                color="purple"
                size="md"
                :disabled="canGrant === false || processing"
                @click="grant"
            >
                {{ t("vue.kiosk_ai_quota_grant") }}
            </AtomicButton>
        </div>
    </div>
</template>

<script lang="ts" setup>
import { computed, ref } from "vue";
import { Link, router } from "@inertiajs/vue3";
import route from "ziggy-js";
import AtomicButton from "@/components/Atomic/Buttons/AtomicButton.vue";
import NumberInput from "@/components/Atomic/Inputs/NumberInput.vue";
import { useTranslations } from "@/composables/useTranslations";
import { formatCount } from "../formatters";
import type { IAiTrialQuota } from "../types";

const MIN_GRANT = 1;
const MAX_GRANT = 1000;

const props = defineProps<{
    quota: IAiTrialQuota;
    teamId: number;
}>();

const { t } = useTranslations();

const amount = ref<number>(props.quota.default_limit);
const processing = ref<boolean>(false);
const expanded = ref<boolean>(false);

const canGrant = computed<boolean>(
    () => props.quota.action_state === "grantable",
);

const isApplicable = computed<boolean>(
    () => props.quota.action_state !== "not_applicable",
);

// A team the cap does not govern shows only its headline until asked for more.
const collapsed = computed<boolean>(
    () => isApplicable.value === false && expanded.value === false,
);

const stateLabel = computed<string>(() =>
    props.quota.limit_reached
        ? t("vue.kiosk_ai_quota_state_blocked")
        : t("vue.kiosk_ai_quota_state_open"),
);

const stateClass = computed<string>(() =>
    props.quota.limit_reached
        ? "bg-red-200 text-red-800"
        : "bg-green-200 text-green-800",
);

const reason = computed<string>(() => {
    if (props.quota.action_state === "not_reached") {
        return t("vue.kiosk_ai_quota_not_reached");
    }

    if (props.quota.action_state === "needs_trial_extension") {
        return t("vue.kiosk_ai_quota_needs_trial_extension");
    }

    if (props.quota.action_state === "not_applicable" && expanded.value) {
        return t("vue.kiosk_ai_quota_not_applicable");
    }

    return "";
});

const grant = (): void => {
    if (canGrant.value === false) {
        return;
    }

    processing.value = true;

    router.post(
        route("kiosk.teams.ai-quota.store", { team: props.teamId }),
        { amount: Number(amount.value) },
        {
            preserveScroll: true,
            onFinish: () => {
                processing.value = false;
            },
        },
    );
};
</script>
